/* @refresh skip */
// src/lang/LangProvider.jsx
import React, { createContext, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";

import articlesIT from "../components/Articles.it";
import articlesEN from "../components/Articles.en";

export const LangContext = createContext({
  lang: "it",
  t: (k) => k,
  articles: [],
});

// Dizionari UI
const uiIT = {
  kb: {
    title: "Knowledge Base",
    searchPlaceholder: "Cerca negli articoli…",
    readTime: "di lettura",
    emptyTitle: "Nessun articolo trovato",
    emptyDesc: "Prova a modificare i termini di ricerca o i filtri.",
    readMore: "Leggi di più",
    tags: {
      all: "Tutti",
      // Tag effettivamente usati negli articoli IT
      etf: "ETF & Asset Class",
      ai: "AI & Data Science",
      quant: "Strategie Quantitative",
      educazione: "Educazione Finanziaria",
      macro: "Macro Economia",
      consulenza: "Consulenza",
      normativa: "Normativa",
      "deep learning": "Deep Learning",
      allocazione: "Allocazione",
      "asset allocation": "Asset Allocation",
      psicologia: "Psicologia",
      "capitale umano": "Capitale Umano",
      valute: "Valute",
      factor: "Factor Investing",
      finanza: "Finanza",
      "risk management": "Risk Management",
      ocf: "OCF",
      esame: "Esame",
      "consulenti finanziari": "Consulenti Finanziari",
      guida: "Guida",
      formazione: "Formazione",
      // Tag del batch derivati + metriche + market update (IT)
      derivati: "Derivati",
      future: "Futures",
      opzioni: "Opzioni",
      mercati: "Mercati",
      margini: "Margini",
      leva: "Leva",
      hedging: "Hedging",
      rischio: "Rischio",
      certificati: "Certificati",
      "covered warrant": "Covered Warrant",
      "fiscalità": "Fiscalità",
      metriche: "Metriche",
      sharpe: "Sharpe",
      backtest: "Backtest",
      tassi: "Tassi",
    },
  },
  nav: { home: "Home", kb: "Knowledge Base" },
  article: { back: "← Torna alla Knowledge Base" },
  about: { title: "Chi Siamo" },
  solutions: {
    titlePRE: "Metodologie e Casi Studio (educational)",
    titlePOST: "Soluzioni Quantitative Su Misura",
  },
};

const uiEN = {
  kb: {
    title: "Knowledge Base",
    searchPlaceholder: "Search articles…",
    readTime: "read",
    emptyTitle: "No articles found",
    emptyDesc: "Try changing search terms or filters.",
    readMore: "Read more",
    tags: {
      all: "All",
      // Tag effettivamente usati negli articoli EN
      etf: "ETF & Asset Classes",
      ai: "AI & Data Science",
      quant: "Quant Strategies",
      education: "Financial Education",
      macro: "Macro Economics",
      advice: "Financial Advice",
      regulation: "Regulation",
      "deep learning": "Deep Learning",
      allocation: "Allocation",
      "asset allocation": "Asset Allocation",
      psychology: "Behavioral Finance",
      currencies: "Currencies",
      "human capital": "Human Capital",
      factor: "Factor Investing",
      finance: "Finance",
      "risk management": "Risk Management",
      // Tags from the derivatives + metrics + market-update batch (EN)
      derivatives: "Derivatives",
      futures: "Futures",
      options: "Options",
      markets: "Markets",
      margin: "Margin",
      leverage: "Leverage",
      hedging: "Hedging",
      risk: "Risk",
      certificates: "Certificates",
      "covered warrants": "Covered Warrants",
      tax: "Tax",
      metrics: "Metrics",
      sharpe: "Sharpe",
      backtesting: "Backtesting",
      rates: "Rates",
    },
  },
  nav: { home: "Home", kb: "Knowledge Base" },
  article: { back: "← Back to Knowledge Base" },
  about: { title: "About" },
  solutions: {
    titlePRE: "Methods & Case Studies (educational)",
    titlePOST: "Custom Quant Solutions",
  },
};

function normalizeLang(l) {
  return l === "it" || l === "en" ? l : "it";
}

export function LangProvider({ children }) {
  const { lang } = useParams();
  const location = useLocation();

  const safeLang = normalizeLang(lang);
  const isValid = lang === safeLang;

  // SEMPRE al top-level
  useEffect(() => {
    document.documentElement.lang = safeLang;
  }, [safeLang]);

  useEffect(() => {
    localStorage.setItem("lang", safeLang);
  }, [safeLang]);

  const ui = safeLang === "en" ? uiEN : uiIT;

  // t("a.b.c") => ui.a.b.c || chiave originale
  const t = useCallback(
    (key) => key.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), ui) ?? key,
    [ui]
  );

  const articles = useMemo(
    () => (safeLang === "en" ? articlesEN : articlesIT),
    [safeLang]
  );

  const value = useMemo(
    () => ({ lang: safeLang, t, articles }),
    [safeLang, t, articles]
  );

  // redirect calcolato dopo gli hook
  const redirectTo = useMemo(() => {
    if (isValid) return null;
    const newPath = location.pathname.replace(/^\/[^/]+/, `/${safeLang}`);
    return `${newPath}${location.search}${location.hash}`;
  }, [isValid, safeLang, location.pathname, location.search, location.hash]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}
